import "./FiveKeysWheel.scss";

import React from "react";
import clsx from "clsx";

import * as strings from "../../../strings";
import Text from "../../common/Text/Text";
import { FiveKeysBreakdown } from "../../../lib/types/JourneyTypes";
import { Translatable } from "../../../lib/util/Translatable";
import { useTranslate } from "../../../lib/hook/LocaleHooks";

const KEYS: {
	key: keyof FiveKeysBreakdown;
	color: string;
	label: Translatable;
	description: Translatable;
}[] = [
	{
		key: "aimHighInSteering",
		color: "#76EC44",
		label: strings.WHEEL_AIM_HIGH_IN_STEERING,
		description: strings.WHEEL_AIM_HIGH_IN_STEERING_DESCRIPTION,
	},
	{
		key: "getTheBigPicture",
		color: "#67FF9C",
		label: strings.WHEEL_GET_THE_BIG_PICTURE,
		description: strings.WHEEL_GET_THE_BIG_PICTURE_DESCRIPTION,
	},
	{
		key: "keepYourEyesMoving",
		color: "#3AD5D7",
		label: strings.WHEEL_KEEP_YOUR_EYES_MOVING,
		description: strings.WHEEL_KEEP_YOUR_EYES_MOVING_DESCRIPTION,
	},
	{
		key: "leaveYourselfAnOut",
		color: "#71EBFF",
		label: strings.WHEEL_LEAVE_YOURSELF_AN_OUT,
		description: strings.WHEEL_LEAVE_YOURSELF_AN_OUT_DESCRIPTION,
	},
	{
		key: "makeSureTheySeeYou",
		color: "#39ACEA",
		label: strings.WHEEL_MAKE_SURE_THEY_SEE_YOU,
		description: strings.WHEEL_MAKE_SURE_THEY_SEE_YOU_DESCRIPTION,
	},
];

const CENTER = 58;
const INNER_RADIUS = 28.4;
const RING_STEP = 6.83;
const STROKE_WIDTH = 4.55;

const clampScore = (value?: number) =>
	Math.max(0, Math.min(100, Math.round(value || 0)));

export const FiveKeysLegend: React.FC<{ fiveKeys: FiveKeysBreakdown }> = ({
	fiveKeys,
}) => {
	const translate = useTranslate();

	return (
		<ul className={"FiveKeysWheel__legend"}>
			{KEYS.map(({ key, color, label, description }) => (
				<li
					className={"FiveKeysWheel__legend-item"}
					key={key}
					title={translate(description)}
				>
					<span
						className={"FiveKeysWheel__legend-dot"}
						style={{ backgroundColor: color }}
					/>
					<Text className={"FiveKeysWheel__legend-label"}>{label}</Text>
					<Text className={"FiveKeysWheel__legend-value"}>
						{clampScore(fiveKeys[key])}
					</Text>
				</li>
			))}
		</ul>
	);
};

export interface FiveKeysWheelProps {
	fiveKeys: FiveKeysBreakdown;
	score: React.ReactNode;
	className?: string;
}

const FiveKeysWheel: React.FC<FiveKeysWheelProps> = ({
	fiveKeys,
	score,
	className,
}) => {
	return (
		<div
			className={clsx(
				"SingleScore__score-container FiveKeysWheel__chart",
				className,
			)}
		>
			<svg
				className={"FiveKeysWheel__rings"}
				viewBox={`0 0 ${CENTER * 2} ${CENTER * 2}`}
				role={"img"}
			>
				<g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
					{KEYS.map(({ key, color }, index) => {
						const radius = INNER_RADIUS + index * RING_STEP;
						const circumference = 2 * Math.PI * radius;
						const value = clampScore(fiveKeys[key]);
						const arc = (circumference * value) / 100;

						return (
							<React.Fragment key={key}>
								<circle
									className={"FiveKeysWheel__track"}
									cx={CENTER}
									cy={CENTER}
									r={radius}
									strokeWidth={STROKE_WIDTH}
								/>
								<circle
									className={"FiveKeysWheel__value"}
									cx={CENTER}
									cy={CENTER}
									r={radius}
									stroke={color}
									strokeWidth={STROKE_WIDTH}
									strokeLinecap={"round"}
									strokeDasharray={`${arc} ${circumference}`}
								/>
							</React.Fragment>
						);
					})}
				</g>
			</svg>
			<Text className={"SingleScore__score --circles"}>{score}</Text>
		</div>
	);
};

export default React.memo(FiveKeysWheel);
